import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  FormHelperText,
  Switch,
  Alert,
  Snackbar,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Remove as RemoveIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material';
import { useLists, List } from '../hooks';
import { ListItem, productsService, Product, Title } from '../services/firebaseFunctions';
import { useTitles } from '../hooks/useTitles';
import { formatFirestoreDateOnly } from '../utils/dateUtils';
import { useAuth } from '../contexts/AuthContext';
import { operationErrorMessage } from '../utils/adminFeedback';
import DestructiveActionDialog from '../components/DestructiveActionDialog';

const ListsPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('listas', 'editar');
  const { lists, loading, error, createList, updateList, deleteList } = useLists();
  const { titles } = useTitles();

  // Products para o seletor de productId (T112)
  const [products, setProducts] = useState<Product[]>([]);
  const [productsLoading, setProductsLoading] = useState(true);

  useEffect(() => {
    productsService
      .getAll()
      .then(setProducts)
      .catch(() => {
        // Erro silencioso no carregamento de products — não bloqueia a página
        setProducts([]);
      })
      .finally(() => setProductsLoading(false));
  }, []);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingList, setEditingList] = useState<List | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true,
    items: [] as ListItem[],
    productId: '',
  });
  const [productIdError, setProductIdError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });
  const [submitting, setSubmitting] = useState(false);
  const [deletingListId, setDeletingListId] = useState<string | null>(null);
  const [listPendingDeletion, setListPendingDeletion] = useState<List | null>(null);
  const [selectedTitle, setSelectedTitle] = useState<Title | null>(null);
  const [titleProductFilterId, setTitleProductFilterId] = useState('');

  const selectedProduct = products.find((p) => p.id === formData.productId) ?? null;
  const filterProducts = selectedProduct ? products.filter((p) => p.tipo === selectedProduct.tipo) : [];
  const filteredTitles = (selectedProduct
    ? titles.filter((t) => t.type === selectedProduct.tipo)
    : titles
  ).filter((t) => !titleProductFilterId || t.productId === titleProductFilterId);

  const titleTypeLabel = (type: Title['type']): string => (type === 'ebook' ? 'E-book' : 'Audiobook');
  const titleTypeColor = (type: Title['type']): 'primary' | 'secondary' => (type === 'ebook' ? 'primary' : 'secondary');
  const titleProduct = (title: Title): Product | undefined => products.find((p) => p.id === title.productId);

  const renderTitleChips = (title: Title) => {
    const linkedProduct = titleProduct(title);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Chip label={titleTypeLabel(title.type)} color={titleTypeColor(title.type)} size="small" />
        {linkedProduct && <Chip label={linkedProduct.titulo} variant="outlined" size="small" />}
      </Box>
    );
  };

  const addedTitleIds = new Set(formData.items.map((item) => item.titleId));
  const availableTitles = [...filteredTitles]
    .filter((title) => !addedTitleIds.has(title.id))
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));

  const handleOpenDialog = (list?: List) => {
    setProductIdError(null);
    setSelectedTitle(null);
    setTitleProductFilterId('');
    if (list) {
      setEditingList(list);
      setFormData({
        name: list.name,
        description: list.description || '',
        isActive: list.isActive,
        items: list.items || [],
        productId: list.productId || '',
      });
    } else {
      setEditingList(null);
      setFormData({
        name: '',
        description: '',
        isActive: true,
        items: [],
        productId: '',
      });
    }
    setOpenDialog(true);
  };

  const resetListsDialog = () => {
    setOpenDialog(false);
    setEditingList(null);
    setProductIdError(null);
    setSelectedTitle(null);
    setTitleProductFilterId('');
  };

  const handleCloseDialog = () => {
    if (submitting) return;
    resetListsDialog();
  };

  const handleAddItem = () => {
    if (!selectedTitle) return;
    const newItem = {
      titleId: selectedTitle.id,
      order: formData.items.length + 1,
    };
    setFormData({
      ...formData,
      items: [...formData.items, newItem],
    });
    setSelectedTitle(null);
  };

  const handleRemoveItem = (index: number) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    // Reorder items after removal
    const reorderedItems = newItems.map((item, i) => ({
      ...item,
      order: i + 1,
    }));
    setFormData({
      ...formData,
      items: reorderedItems,
    });
  };

  const handleMoveItemUp = (index: number) => {
    if (index === 0) return; // Can't move first item up

    const newItems = [...formData.items];
    const item = newItems[index];
    const prevItem = newItems[index - 1];

    // Swap items
    newItems[index] = prevItem;
    newItems[index - 1] = item;

    // Update order numbers
    newItems[index].order = index + 1;
    newItems[index - 1].order = index;

    setFormData({
      ...formData,
      items: newItems,
    });
  };

  const handleMoveItemDown = (index: number) => {
    if (index === formData.items.length - 1) return; // Can't move last item down

    const newItems = [...formData.items];
    const item = newItems[index];
    const nextItem = newItems[index + 1];

    // Swap items
    newItems[index] = nextItem;
    newItems[index + 1] = item;

    // Update order numbers
    newItems[index].order = index + 1;
    newItems[index + 1].order = index + 2;

    setFormData({
      ...formData,
      items: newItems,
    });
  };

  const handleSubmit = async () => {
    // Mantém a validação existente de productId, inclusive em listas legadas.
    if (!formData.productId) {
      setProductIdError('Selecione um ativo para esta lista.');
      return;
    }
    setProductIdError(null);

    try {
      setSubmitting(true);
      if (editingList) {
        const result = await updateList(editingList.id, {
          ...formData,
          description: formData.description.trim() || null,
        });
        setSnackbar({
          open: true,
          message: result.success ? 'Lista atualizada com sucesso!' : operationErrorMessage('list', result.response),
          severity: result.success ? 'success' : 'error',
        });
        if (result.success) {
          resetListsDialog();
        }
      } else {
        const result = await createList({
          ...formData,
          description: formData.description.trim() || null,
        });
        setSnackbar({
          open: true,
          message: result.success ? 'Lista criada com sucesso!' : operationErrorMessage('list', result.response),
          severity: result.success ? 'success' : 'error',
        });
        if (result.success) {
          resetListsDialog();
        }
      }
    } catch {
      setSnackbar({
        open: true,
        message: operationErrorMessage('list'),
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (list: List) => {
    try {
      setDeletingListId(list.id);
      const result = await deleteList(list.id);
      setSnackbar({
        open: true,
        message: result.success ? 'Lista excluída com sucesso!' : operationErrorMessage('list', result.response),
        severity: result.success ? 'success' : 'error',
      });
      if (result.success) setListPendingDeletion(null);
    } catch {
      setSnackbar({
        open: true,
        message: operationErrorMessage('list'),
        severity: 'error',
      });
    } finally {
      setDeletingListId(null);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Gerenciar Listas
        </Typography>
        <Tooltip title={canManage ? '' : 'Você não tem permissão de edição no módulo Listas.'}>
          <span>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()} disabled={!canManage}>
              Nova Lista
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell>Ativo</TableCell>
                <TableCell>Itens</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Criado em</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lists.map((list) => {
                const linkedProduct = products.find((p) => p.id === list.productId);
                return (
                  <TableRow key={list.id}>
                    <TableCell>{list.name}</TableCell>
                    <TableCell>{list.description || '-'}</TableCell>
                    <TableCell>
                      {linkedProduct ? (
                        <Chip
                          label={linkedProduct.titulo}
                          size="small"
                          color={linkedProduct.isActive ? 'default' : 'warning'}
                        />
                      ) : (
                        <Chip label="Sem ativo" size="small" color="warning" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>{list.items.length}</TableCell>
                    <TableCell>
                      <Chip label={list.isActive ? 'Ativo' : 'Inativo'} color={list.isActive ? 'success' : 'default'} size="small" />
                    </TableCell>
                    <TableCell>{formatFirestoreDateOnly(list.createdAt)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title={canManage ? '' : 'Você não tem permissão de edição no módulo Listas.'}>
                        <span>
                          <IconButton color="primary" onClick={() => handleOpenDialog(list)} disabled={!canManage || deletingListId === list.id}>
                            <EditIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={canManage ? '' : 'Você não tem permissão de edição no módulo Listas.'}>
                        <span>
                          <IconButton color="error" onClick={() => {
                            if (list.productId) setListPendingDeletion(list);
                            else void handleDelete(list);
                          }} disabled={!canManage || deletingListId === list.id}>
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog para criar/editar lista */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingList ? 'Editar Lista' : 'Nova Lista'}</DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nome"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  margin="normal"
                  required
                  disabled={submitting}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descrição"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  margin="normal"
                  multiline
                  rows={3}
                  disabled={submitting}
                />
              </Grid>

              {/* Seletor de productId — T112 */}
              <Grid item xs={12}>
                <FormControl
                  fullWidth
                  margin="normal"
                  disabled={!!editingList || submitting || productsLoading}
                  error={!!productIdError}
                  required
                >
                  <InputLabel>Ativo</InputLabel>
                  <Select
                    value={formData.productId}
                    onChange={(e) => {
                      setProductIdError(null);
                      const nextProduct = products.find((p) => p.id === e.target.value);
                      const currentFilter = products.find((p) => p.id === titleProductFilterId);
                      if (currentFilter && currentFilter.tipo !== nextProduct?.tipo) setTitleProductFilterId('');
                      setFormData({ ...formData, productId: e.target.value });
                    }}
                    label="Ativo"
                  >
                    {products.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.titulo}
                        {!p.isActive && ' (inativo)'}
                      </MenuItem>
                    ))}
                    {products.length === 0 && !productsLoading && (
                      <MenuItem disabled value="">
                        Nenhum ativo disponível
                      </MenuItem>
                    )}
                  </Select>
                  {productIdError && (
                    <FormHelperText>{productIdError}</FormHelperText>
                  )}
                  {editingList && (
                    <FormHelperText>Ativo vinculado não pode ser alterado após a criação da lista.</FormHelperText>
                  )}
                  {!formData.productId && !editingList && (
                    <FormHelperText>Lista legada sem ativo — selecione para vincular.</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {/* Seção de Itens */}
              <Grid item xs={12}>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Itens da Lista
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Autocomplete<Title, false, false, false>
                      options={availableTitles}
                      value={selectedTitle}
                      onChange={(_, newValue) => setSelectedTitle(newValue)}
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      noOptionsText="Nenhum título disponível"
                      disabled={submitting || !formData.productId}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} key={option.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                          <span>{option.name}</span>
                          {renderTitleChips(option)}
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} size="small" label="Buscar título" placeholder="Digite o nome do título" />
                      )}
                      componentsProps={{
                        popper: {
                          placement: 'bottom-start',
                          modifiers: [{ name: 'flip', enabled: false }],
                        },
                      }}
                      sx={{ flex: 1 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 200 }} disabled={submitting || !formData.productId}>
                      <InputLabel id="title-product-filter-label" shrink>Filtrar por ativo</InputLabel>
                      <Select
                        labelId="title-product-filter-label"
                        label="Filtrar por ativo"
                        displayEmpty
                        notched
                        value={titleProductFilterId}
                        onChange={(e) => setTitleProductFilterId(e.target.value)}
                      >
                        <MenuItem value="">Todos</MenuItem>
                        {filterProducts.map((p) => (
                          <MenuItem key={p.id} value={p.id}>
                            {p.titulo}
                            {!p.isActive && ' (inativo)'}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleAddItem}
                      size="large"
                      disabled={submitting || !formData.productId || !selectedTitle}
                    >
                      Adicionar Item
                    </Button>
                  </Box>

                  {formData.items.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      Nenhum item adicionado. Clique em "Adicionar Item" para começar.
                    </Typography>
                  ) : (
                    <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                      {formData.items.map((item, index) => {
                        const itemTitle = titles.find((t) => t.id === item.titleId);
                        return (
                          <Box
                            key={index}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 2,
                              p: 2,
                              border: '1px solid',
                              borderColor: 'grey.300',
                              borderRadius: 1,
                              bgcolor: 'grey.50',
                            }}
                          >
                            <Typography variant="body2" sx={{ minWidth: 40, fontWeight: 'bold' }}>
                              #{item.order}
                            </Typography>

                            {/* Order controls */}
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              <IconButton
                                size="small"
                                onClick={() => handleMoveItemUp(index)}
                                disabled={submitting || index === 0}
                                sx={{ p: 0.5 }}
                              >
                                <ArrowUpIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleMoveItemDown(index)}
                                disabled={submitting || index === formData.items.length - 1}
                                sx={{ p: 0.5 }}
                              >
                                <ArrowDownIcon fontSize="small" />
                              </IconButton>
                            </Box>

                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2">
                                {itemTitle?.name ?? 'Título não encontrado'}
                              </Typography>
                              {itemTitle && renderTitleChips(itemTitle)}
                            </Box>

                            <IconButton color="error" onClick={() => handleRemoveItem(index)} size="small" disabled={submitting}>
                              <RemoveIcon />
                            </IconButton>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      disabled={submitting}
                    />
                  }
                  label="Ativo"
                  sx={{ mt: 2 }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {submitting ? (editingList ? 'Salvando…' : 'Criando…') : editingList ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      <DestructiveActionDialog
        open={Boolean(listPendingDeletion)}
        entityLabel="Lista"
        entityName={listPendingDeletion?.name || ''}
        busy={Boolean(listPendingDeletion && deletingListId === listPendingDeletion.id)}
        onCancel={() => setListPendingDeletion(null)}
        onConfirm={() => listPendingDeletion && void handleDelete(listPendingDeletion)}
      />

      {/* Snackbar para mensagens */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ListsPage;
